import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout, theme } from 'antd';
import { DashboardOutlined, TeamOutlined, MessageOutlined, BellOutlined, BarChartOutlined, CreditCardOutlined, DollarOutlined, KeyOutlined, DatabaseOutlined, RobotOutlined, SettingOutlined, CodeOutlined, PhoneOutlined, ApiOutlined, HeartOutlined } from '@ant-design/icons';
import { AppSider, type SiderMenuGroup } from '@/shared/components/AppSider';
import { AppHeader } from '@/shared/components/AppHeader';
import { ImpersonationBanner } from '@/shared/components/ImpersonationBanner';
import type { CommandItem } from '@/shared/components/CommandPalette';

const { Content } = Layout;

const ADMIN_GROUPS: SiderMenuGroup[] = [
  {
    title: 'Überblick',
    items: [
      { key: '/admin/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
      { key: '/admin/analytics', icon: <BarChartOutlined />, label: 'Analytics' },
    ],
  },
  {
    title: 'Kunden',
    items: [
      { key: '/admin/tenants', icon: <TeamOutlined />, label: 'Tenants' },
      { key: '/admin/sessions', icon: <MessageOutlined />, label: 'Sessions' },
    ],
  },
  {
    title: 'Monitoring',
    items: [
      { key: '/admin/alerts', icon: <BellOutlined />, label: 'Alerts' },
      { key: '/admin/ingestion-health', icon: <HeartOutlined />, label: 'Ingestion-Health' },
    ],
  },
  {
    title: 'Abrechnung',
    items: [
      { key: '/admin/billing', icon: <CreditCardOutlined />, label: 'Billing' },
      { key: '/admin/pricing', icon: <DollarOutlined />, label: 'Preise' },
      { key: '/admin/integrations', icon: <ApiOutlined />, label: 'Integrationen' },
    ],
  },
];

/** Pfad-Segmente -> lesbare Labels fuer Breadcrumbs */
const PATH_LABELS: Record<string, string> = {
  admin: 'Admin',
  dashboard: 'Dashboard',
  tenants: 'Tenants',
  sessions: 'Sessions',
  alerts: 'Alerts',
  analytics: 'Analytics',
  billing: 'Billing',
  pricing: 'Preise',
  integrations: 'Integrationen',
  replay: 'Replay',
  'ingestion-health': 'Ingestion-Health',
};

const COMMANDS: CommandItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', hint: '/admin/dashboard', icon: <DashboardOutlined />, group: 'Navigation', to: '/admin/dashboard', keywords: ['overview', 'uebersicht'] },
  { id: 'nav-tenants', label: 'Tenants', hint: '/admin/tenants', icon: <TeamOutlined />, group: 'Navigation', to: '/admin/tenants', keywords: ['kunden', 'customer'] },
  { id: 'nav-sessions', label: 'Sessions', hint: '/admin/sessions', icon: <MessageOutlined />, group: 'Navigation', to: '/admin/sessions', keywords: ['chats', 'gespraeche'] },
  { id: 'nav-analytics', label: 'Analytics', hint: '/admin/analytics', icon: <BarChartOutlined />, group: 'Navigation', to: '/admin/analytics', keywords: ['statistik', 'charts'] },
  { id: 'nav-alerts', label: 'Alerts', hint: '/admin/alerts', icon: <BellOutlined />, group: 'Navigation', to: '/admin/alerts', keywords: ['warnungen', 'benachrichtigungen'] },
  { id: 'nav-ingestion-health', label: 'Ingestion-Health', hint: '/admin/ingestion-health', icon: <HeartOutlined />, group: 'Navigation', to: '/admin/ingestion-health', keywords: ['readyz', 'health', 'embedding', 'canary', 'probe'] },
  { id: 'nav-billing', label: 'Billing', hint: '/admin/billing', icon: <CreditCardOutlined />, group: 'Navigation', to: '/admin/billing', keywords: ['abrechnung', 'rechnungen'] },
  { id: 'nav-pricing', label: 'Preise', hint: '/admin/pricing', icon: <DollarOutlined />, group: 'Navigation', to: '/admin/pricing', keywords: ['pricing', 'tarife', 'modelle'] },
  { id: 'nav-integrations', label: 'Integrationen', hint: '/admin/integrations', icon: <ApiOutlined />, group: 'Navigation', to: '/admin/integrations', keywords: ['mollie', 'payment', 'api-key', 'zahlung'] },

  { id: 'portal-overview', label: 'Zum Kunden-Dashboard wechseln', hint: '/dashboard/overview', icon: <DashboardOutlined />, group: 'Wechsel', to: '/dashboard/overview' },
  { id: 'portal-datasources', label: 'Datenquellen (Portal)', hint: '/dashboard/datasources', icon: <DatabaseOutlined />, group: 'Wechsel', to: '/dashboard/datasources' },
  { id: 'portal-api-keys', label: 'API-Keys (Portal)', hint: '/dashboard/api-keys', icon: <KeyOutlined />, group: 'Wechsel', to: '/dashboard/api-keys' },
  { id: 'portal-config', label: 'Einstellungen (Portal)', hint: '/dashboard/config', icon: <SettingOutlined />, group: 'Wechsel', to: '/dashboard/config' },
  { id: 'portal-chat-test', label: 'Chat testen', hint: '/dashboard/chat-test', icon: <RobotOutlined />, group: 'Wechsel', to: '/dashboard/chat-test' },
  { id: 'portal-embed', label: 'Widget einbetten', hint: '/dashboard/embed', icon: <CodeOutlined />, group: 'Wechsel', to: '/dashboard/embed' },
  { id: 'portal-handover', label: 'Handover-Konfiguration', hint: '/dashboard/handover', icon: <PhoneOutlined />, group: 'Wechsel', to: '/dashboard/handover' },
];

export function AdminLayout(): React.ReactElement {
  const { token } = theme.useToken();
  const [collapsed, setCollapsed] = useState<boolean>(false);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppSider variant='dark' accent='#6366f1' logoInitials='SR' title='Schnitzraum' subtitle='ADMIN' collapsed={collapsed} onCollapseChange={setCollapsed} groups={ADMIN_GROUPS} statusLabel='Alle Systeme online' statusVariant='ok' version='v1.0.0' />

      <Layout>
        <ImpersonationBanner />
        <AppHeader collapsed={collapsed} onToggleCollapsed={() => setCollapsed((v) => !v)} commands={COMMANDS} roleLabel='Super-Admin' accent='#6366f1' pathLabels={PATH_LABELS} />

        <Content
          style={{
            padding: 24,
            background: token.colorBgLayout,
            overflowY: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
